import { ViewportScroller } from "@angular/common";
import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, from, Observable } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { VirkeskopAffarsformer } from "~app/common/models/constants";
import { SearchKontraktResponse } from "~app/common/models/virkeskop/v2/SearchKontraktResponse";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { IVKSearchFilter, IVKSearchResultWithFilter, SearchKontraktResponseExtensions } from "../models/kontrakt-search-filter-model";
import { KONTRAKT_SEARCH_KEY } from "../sok-anbud-kontrakt/kontrakt-search-mina-filter/kontrakt-token-keys";

@Injectable({
    providedIn: "root"
})
export class AnbudKontraktSearchService {

    readonly WEB_INSTALLNINGAR_KEY_RECENT;

    /**
     * Uppdateras i realtid när sökkriterier ändras av användaren
     */
    searchFilter$ = new BehaviorSubject<IVKSearchFilter>(null);

    /**
     * Används för att skicka ny komplett mall till sökfilterkomponenterna
     */
    searchFilterTemplate$ = new BehaviorSubject<IVKSearchFilter>(null);

    /**
     * Uppdateras när en sökning görs. Objekt med sökresultat och vilket filter som användes.
     */
    searchResult$ = new BehaviorSubject<IVKSearchResultWithFilter>({
        filter: null,
        result: []
    });

    private readonly busyService = inject(BusyService);
    private readonly viewportScroller = inject(ViewportScroller);
    private readonly webinstallningarService = inject(WebinstallningarService);

    private recentScrollPosition: [number, number] = null;

    constructor() {
        const key = inject(KONTRAKT_SEARCH_KEY);

        this.WEB_INSTALLNINGAR_KEY_RECENT = key;
    }

    applyFilter(filter: IVKSearchFilter) {
        this.searchFilterTemplate$.next(filter);
        this.searchFilter$.next(filter);
    }

    clearResults() {
        this.searchComplete({
            filter: this.searchResult$.getValue().filter,
            result: []
        });
    }

    getRecentFilter(): Observable<IVKSearchFilter> {
        return from(this.webinstallningarService.getAsync<IVKSearchFilter>(this.WEB_INSTALLNINGAR_KEY_RECENT));
    }

    getRecentScrollPosition(): [number, number] {
        return this.recentScrollPosition;
    }

    saveRecentScrollPosition(): void {
        this.recentScrollPosition = this.viewportScroller.getScrollPosition();
    }

    search(observable: Observable<SearchKontraktResponse[]>) {
        this.clearResults();
        this.busyService.addWork(observable)
            .subscribe((result: (SearchKontraktResponse & SearchKontraktResponseExtensions)[]): void => {
                const filter = this.searchFilter$.getValue();
                this.webinstallningarService.set<IVKSearchFilter>(this.WEB_INSTALLNINGAR_KEY_RECENT, filter);

                result.forEach(x => {
                    x.affarsform = this.affarsformShort(x.affarsform);
                });

                this.searchComplete({
                    filter,
                    result
                });

                this.scrollToRecentPos();
            });
    }

    searchComplete(blob: IVKSearchResultWithFilter) {
        this.searchResult$.next(blob);
    }

    searchCriteriaChanged(filter: IVKSearchFilter) {
        this.searchFilter$.next(filter);
    }

    private affarsformShort(affarsform: string): string {
        return VirkeskopAffarsformer.AllaWithShortType.find(x => x.value === affarsform)?.key ?? "-";
    }

    private scrollToRecentPos() {
        if (this.recentScrollPosition) {
            setTimeout(() => {
                this.viewportScroller.scrollToPosition(this.recentScrollPosition);
                this.recentScrollPosition = null;
            }, 100);
        }
    }
}
